import { Component, inject, OnInit, OnDestroy, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, takeUntil, filter } from 'rxjs';
import { NotificationService } from '../../../services/api/notification.service';
import { INotificationResponse } from '../../../interfaces/notification.interface';
import { ClickOutsideDirective } from '../../directives/click-outside.directive';
import { IconModule } from '../../icon/icon.module';
import { AlertPopupComponent, AlertPopupData } from '../alert-popup/alert-popup.component';
import { AuthService } from '../../../auth/services/auth.service';
import { DocumentNavigationService } from '../../services/document-navigation.service';
import { PanicEventsService } from '../../../services/api/panic-events.service';
import { MobileLifecycleService } from '../../../services/mobile/mobile-lifecycle.service';

@Component({
    selector: 'lc-notifications',
    standalone: true,
    imports: [CommonModule, ClickOutsideDirective, IconModule, AlertPopupComponent],
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.component.scss'],
})
export class NotificationsComponent implements OnInit, OnDestroy {
    private readonly notificationService = inject(NotificationService);
    private readonly authService = inject(AuthService);
    private readonly documentNavigationService = inject(DocumentNavigationService);
    private readonly panicEventsService = inject(PanicEventsService);
    private readonly mobileLifecycle = inject(MobileLifecycleService);
    private readonly router = inject(Router);

    // Signals para reatividade
    readonly notifications = this.notificationService.notificationsListCache;
    readonly unreadCount = this.notificationService.getUnreadCount;
    readonly isLoading = signal(false);

    // Computed para separar notificações
    readonly unreadNotifications = computed(() => this.notifications().filter(n => !n.isRead));
    readonly readNotifications = computed(() => this.notifications().filter(n => n.isRead));

    // Estado do dropdown
    readonly isDropdownOpen = signal(false);

    // Estado das abas - agora com suporte a entityType
    readonly activeTab = signal<'unread' | 'read' | string>('unread');
    readonly activeEntityType = signal<string | null>(null);

    // Estado do filtro de busca
    readonly searchQuery = signal<string>('');

    // Estado dos filtros (mostrar/ocultar)
    readonly showSearch = signal<boolean>(false);
    readonly showEntityFilters = signal<boolean>(false);

    // Computed para obter tipos de entidade únicos das notificações
    readonly availableEntityTypes = computed(() => {
        const allNotifications = this.notifications();
        const entityTypes = new Set<string>();

        allNotifications.forEach(notification => {
            if (notification.entityType) {
                entityTypes.add(notification.entityType);
            }
        });

        return Array.from(entityTypes).sort();
    });

    // Computed para notificações filtradas por tipo de entidade
    // A busca por texto agora é feita no backend
    readonly filteredNotifications = computed(() => {
        const searchQuery = this.searchQuery().toLowerCase().trim();

        // Se está buscando no backend, não aplicar filtro local por busca
        // O backend já retorna os resultados filtrados
        if (searchQuery && !this.isSearchingBackend()) {
            // Quando há busca, mostrar todas as notificações (backend já filtrou)
            const notifications = this.notifications();
            const entityType = this.activeEntityType();

            // Apenas aplicar filtro de entityType se ativo
            if (entityType) {
                return notifications.filter(n => n.entityType === entityType);
            }

            return notifications;
        }

        // Sem busca: aplicar filtro por aba (read/unread) e entityType
        const notifications = this.activeTab() === 'unread' ? this.unreadNotifications() : this.readNotifications();
        const entityType = this.activeEntityType();

        // Filtro por tipo de entidade
        if (entityType) {
            return notifications.filter(n => n.entityType === entityType);
        }

        return notifications;
    });

    // Computed para contagem de resultados da busca
    readonly searchResultsCount = computed(() => {
        const searchQuery = this.searchQuery().trim();
        if (!searchQuery) return null;

        const filtered = this.filteredNotifications();
        return filtered.length;
    });

    // Computed para contar notificações por tipo de entidade
    readonly entityTypeCounts = computed(() => {
        const allNotifications = this.notifications();
        const counts: { [key: string]: { total: number; unread: number; read: number } } = {};

        allNotifications.forEach(notification => {
            if (notification.entityType) {
                if (!counts[notification.entityType]) {
                    counts[notification.entityType] = { total: 0, unread: 0, read: 0 };
                }

                counts[notification.entityType].total++;

                if (notification.isRead) {
                    counts[notification.entityType].read++;
                } else {
                    counts[notification.entityType].unread++;
                }
            }
        });

        return counts;
    });

    // Método para obter contagem de um tipo específico
    getEntityTypeCount(entityType: string): { total: number; unread: number; read: number } {
        return this.entityTypeCounts()[entityType] || { total: 0, unread: 0, read: 0 };
    }

    // Método para obter contagem baseada na aba ativa
    getEntityTypeCountForActiveTab(entityType: string): number {
        const counts = this.getEntityTypeCount(entityType);

        if (this.activeTab() === 'unread') {
            return counts.unread;
        } else if (this.activeTab() === 'read') {
            return counts.read;
        }

        return counts.total;
    }

    // Método para obter contagem total baseada na aba ativa
    getTotalCountForActiveTab(): number {
        if (this.activeTab() === 'unread') {
            return this.unreadNotifications().length;
        } else if (this.activeTab() === 'read') {
            return this.readNotifications().length;
        }

        return this.notifications().length;
    }

    // Estado do popup de alerta (fila — múltiplos pânicos simultâneos)
    readonly alertPopupQueue = signal<AlertPopupData[]>([]);
    readonly hasShownInitialAlert = signal(false);

    // Subject para debounce da busca
    private readonly searchSubject = new Subject<string>();
    private readonly destroy$ = new Subject<void>();

    // Flag para indicar se está buscando no backend
    readonly isSearchingBackend = signal(false);

    // Computed para mostrar badge
    readonly showBadge = computed(() => this.unreadCount() > 0);
    readonly badgeText = computed(() => {
        const count = this.unreadCount();
        return count.toString();
    });

    ngOnInit(): void {
        // Carrega notificações e só então verifica popups pendentes (sem race de timer)
        this.loadNotifications({ checkAlerts: true });

        // Escutar novas notificações em tempo real
        this.notificationService.onNewNotification.subscribe(notification => {
            this.onNewNotificationReceived(notification);
        });

        // Escutar mudanças de status de panicEvent (alguém atendeu/resolveu)
        this.notificationService.onPanicStatusUpdated.subscribe(payload => {
            this.onPanicStatusUpdated(payload);
        });

        // Ao voltar do background (Android/iOS): refetch + reavaliar popups OPEN
        this.mobileLifecycle.appState$
            .pipe(
                filter(state => state.isActive),
                takeUntil(this.destroy$),
            )
            .subscribe(() => {
                this.loadNotifications({ checkAlerts: true, forceAlertCheck: true });
            });

        // Configurar debounce para busca no backend (300ms)
        this.searchSubject.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            takeUntil(this.destroy$)
        ).subscribe(query => {
            this.performBackendSearch(query);
        });
    }

    ngOnDestroy(): void {
        // Cleanup
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Carrega notificações do servidor.
     * @param checkAlerts — após sucesso, avalia pânico/despacho não lidos
     * @param forceAlertCheck — ignora hasShownInitialAlert (ex.: resume do app)
     */
    private loadNotifications(options?: {
        checkAlerts?: boolean;
        forceAlertCheck?: boolean;
    }): void {
        this.isLoading.set(true);

        this.notificationService.buscarTodas().subscribe({
            next: () => {
                this.isLoading.set(false);
                if (options?.checkAlerts) {
                    this.checkUnreadOccurrenceAlerts(!!options.forceAlertCheck);
                }
            },
            error: (error: any) => {
                console.error('❌ Erro ao carregar notificações:', error);
                this.isLoading.set(false);
            },
        });
    }

    /**
     * Alterna dropdown de notificações
     */
    toggleDropdown(): void {
        const wasOpen = this.isDropdownOpen();
        this.isDropdownOpen.update(open => !open);

        // Se está abrindo o dropdown, recarregar notificações para ter dados atualizados
        if (!wasOpen) {
            this.loadNotifications();
        }
    }

    /**
     * Fecha dropdown
     */
    closeDropdown(): void {
        this.isDropdownOpen.set(false);
    }

    /**
     * Define a aba ativa
     */
    setActiveTab(tab: 'unread' | 'read'): void {
        this.activeTab.set(tab);
        this.activeEntityType.set(null); // Reset entity type filter

        // Se há busca ativa, recarregar com novo filtro
        const query = this.searchQuery();
        if (query && query.trim()) {
            this.performBackendSearch(query);
        }
    }

    /**
     * Define o tipo de entidade ativo
     */
    setActiveEntityType(entityType: string | null): void {
        this.activeEntityType.set(entityType);
    }

    /**
     * Atualiza a query de busca
     */
    updateSearchQuery(query: string): void {
        this.searchQuery.set(query);
    }

    /**
     * Limpa a busca
     */
    clearSearch(): void {
        this.searchQuery.set('');
        // Recarregar notificações sem filtro
        this.loadNotifications();
    }

    /**
     * Manipula o evento de input da busca
     * Dispara busca no backend com debounce
     */
    onSearchInput(event: Event): void {
        const target = event.target as HTMLInputElement;
        const query = target.value;
        this.updateSearchQuery(query);

        // Emitir para o subject (com debounce)
        this.searchSubject.next(query);
    }

    /**
     * Executa busca no backend
     */
    private performBackendSearch(query: string): void {
        // Se query vazia, carregar todas as notificações
        if (!query || !query.trim()) {
            this.loadNotifications();
            return;
        }

        this.isSearchingBackend.set(true);
        this.isLoading.set(true);

        // Buscar no backend usando o novo endpoint (sem limite - busca em toda a base)
        this.notificationService.buscarNotificacoes(query, {
            // Sem limite - o backend busca em todas as notificações quando há query
            isRead: this.activeTab() === 'read' ? true : (this.activeTab() === 'unread' ? false : undefined),
        }).subscribe({
            next: (response: any) => {
                // O NotificationService já atualiza o cache interno automaticamente
                this.isLoading.set(false);
                this.isSearchingBackend.set(false);
            },
            error: (error: any) => {
                console.error('❌ Erro ao buscar notificações:', error);
                this.isLoading.set(false);
                this.isSearchingBackend.set(false);
            },
        });
    }

    /**
     * Alterna a visibilidade da busca
     */
    toggleSearch(): void {
        this.showSearch.update(show => !show);
        if (!this.showSearch()) {
            this.clearSearch();
        }
    }

    /**
     * Alterna a visibilidade dos filtros de tipo
     */
    toggleEntityFilters(): void {
        this.showEntityFilters.update(show => !show);
        if (!this.showEntityFilters()) {
            this.setActiveEntityType(null);
        }
    }

    /**
     * Verifica se uma notificação corresponde à query de busca
     */
    private matchesSearchQuery(notification: INotificationResponse, searchQuery: string): boolean {
        if (!searchQuery) return true;

        const query = searchQuery.toLowerCase();

        // Buscar em campos básicos da notificação
        const basicFields = [notification.title, notification.message, notification.entityType, notification.entityId];

        // Verificar campos básicos
        for (const field of basicFields) {
            if (field && field.toString().toLowerCase().includes(query)) {
                return true;
            }
        }

        // Buscar em campos específicos baseados no tipo de entidade
        if (notification.entityType) {
            const entitySpecificFields = this.getEntitySpecificSearchFields(notification);

            for (const field of entitySpecificFields) {
                if (field && field.toString().toLowerCase().includes(query)) {
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * Obtém campos específicos para busca baseados no tipo de entidade
     */
    private getEntitySpecificSearchFields(notification: INotificationResponse): string[] {
        const fields: string[] = [];

        // Campos comuns que podem aparecer em diferentes tipos de notificação
        const commonFields = [
            'userName',
            'supervisorName',
            'witnessName',
            'applicant',
            'postName',
            'postAddress',
            'occurrenceAddress',
            'description',
            'generalObservation',
            'notes',
            'resolution',
            'reviewNotes',
        ];

        // Adicionar campos comuns se existirem na notificação
        commonFields.forEach(field => {
            if ((notification as any)[field]) {
                fields.push((notification as any)[field]);
            }
        });

        // Campos específicos por tipo de entidade
        switch (notification.entityType) {
            case 'supply':
                fields.push((notification as any).userName || '', (notification as any).postName || '', (notification as any).generalObservation || '');
                break;

            case 'occurrence':
            case 'occurrenceDispatch':
                fields.push(
                    (notification as any).applicant || '',
                    (notification as any).userName || '',
                    (notification as any).supervisorName || '',
                    (notification as any).postName || '',
                    (notification as any).postAddress || '',
                    (notification as any).occurrenceAddress || '',
                    (notification as any).description || '',
                    (notification as any).peopleInvolved || ''
                );
                break;

            case 'patrol':
                fields.push((notification as any).userName || '', (notification as any).description || '', (notification as any).postName || '');
                break;

            case 'shift':
                fields.push((notification as any).userName || '', (notification as any).postName || '');
                break;

            case 'vehicleChecklist':
            case 'motorcycleChecklist':
            case 'doormanChecklist':
                fields.push(
                    (notification as any).userName || '',
                    (notification as any).postName || '',
                    (notification as any).generalObservation || '',
                    (notification as any).reviewNotes || ''
                );
                break;

            case 'motorizedService':
                fields.push(
                    (notification as any).userName || '',
                    (notification as any).supervisorName || '',
                    (notification as any).witnessName || '',
                    (notification as any).postName || '',
                    (notification as any).streetPatrol || '',
                    (notification as any).occurrence || '',
                    (notification as any).vehicleInspection || ''
                );
                break;
        }

        return fields.filter(field => field && field.trim() !== '');
    }

    /**
     * Obtém o nome amigável para o tipo de entidade
     */
    getEntityTypeName(entityType: string): string {
        const names: { [key: string]: string } = {
            supply: 'Abastecimento',
            occurrence: 'Ocorrência',
            occurrenceDispatch: 'Despacho',
            patrol: 'Ronda',
            shift: 'Turno',
            vehicleChecklist: 'Checklist Veículo',
            motorcycleChecklist: 'Checklist Moto',
            motorizedService: 'Serviço Motorizado',
            doormanChecklist: 'Checklist Portaria',
            panicEvent: 'Pânico',
            document: 'Documento',
            foodBasket: 'Cesta Básica',
            disciplinaryWarning: 'Advertência',
            vacationSchedule: 'Escala de Férias',
            termination: 'Desligamento',
        };

        return names[entityType] || entityType;
    }

    /**
     * Obtém ícone para o tipo de entidade
     */
    getEntityTypeIcon(entityType: string): string {
        const icons: { [key: string]: string } = {
            supply: 'fas fa-gas-pump',
            occurrence: 'fas fa-exclamation-triangle',
            occurrenceDispatch: 'fas fa-paper-plane',
            patrol: 'fas fa-walking',
            shift: 'fas fa-clock',
            vehicleChecklist: 'fas fa-car',
            motorcycleChecklist: 'fas fa-motorcycle',
            motorizedService: 'fas fa-truck',
            doormanChecklist: 'fas fa-door-open',
            panicEvent: 'fas fa-exclamation-circle',
            document: 'fas fa-file-alt',
            foodBasket: 'fas fa-shopping-basket',
            disciplinaryWarning: 'fas fa-file-signature',
            vacationSchedule: 'fas fa-calendar-alt',
            termination: 'fas fa-user-slash',
        };

        return icons[entityType] || 'fas fa-bell';
    }

    /**
     * Obtém classes CSS para botão ativo baseado no tipo de entidade
     */
    getActiveButtonClass(entityType: string): string {
        const classes: { [key: string]: string } = {
            supply: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
            occurrence: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
            occurrenceDispatch: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
            patrol: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
            shift: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
            vehicleChecklist: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300',
            motorcycleChecklist: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',
            motorizedService: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300',
            doormanChecklist: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300',
            panicEvent: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
            document: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
        };

        return classes[entityType] || 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
    }

    /**
     * Obtém classes CSS para badge ativo baseado no tipo de entidade
     */
    getActiveBadgeClass(entityType: string): string {
        const classes: { [key: string]: string } = {
            supply: 'bg-green-200 text-green-900 dark:bg-green-800 dark:text-green-200',
            occurrence: 'bg-red-200 text-red-900 dark:bg-red-800 dark:text-red-200',
            occurrenceDispatch: 'bg-orange-200 text-orange-900 dark:bg-orange-800 dark:text-orange-200',
            patrol: 'bg-blue-200 text-blue-900 dark:bg-blue-800 dark:text-blue-200',
            shift: 'bg-purple-200 text-purple-900 dark:bg-purple-800 dark:text-purple-200',
            vehicleChecklist: 'bg-cyan-200 text-cyan-900 dark:bg-cyan-800 dark:text-cyan-200',
            motorcycleChecklist: 'bg-indigo-200 text-indigo-900 dark:bg-indigo-800 dark:text-indigo-200',
            motorizedService: 'bg-pink-200 text-pink-900 dark:bg-pink-800 dark:text-pink-200',
            doormanChecklist: 'bg-teal-200 text-teal-900 dark:bg-teal-800 dark:text-teal-200',
            panicEvent: 'bg-red-200 text-red-900 dark:bg-red-800 dark:text-red-200',
            document: 'bg-amber-200 text-amber-900 dark:bg-amber-800 dark:text-amber-200',
        };

        return classes[entityType] || 'bg-blue-200 text-blue-900 dark:bg-blue-800 dark:text-blue-200';
    }

    /**
     * Marca notificação como lida
     */
    markAsRead(notification: INotificationResponse): void {
        if (!notification.isRead) {
            // Criar uma cópia mutável para atualização local imediata
            const updatedNotification = { ...notification, isRead: true };

            // Atualizar o cache local imediatamente para feedback visual instantâneo
            this.updateNotificationInCache(updatedNotification);

            // Enviar para o backend
            this.notificationService.marcarComoLida(notification.id).subscribe({
                next: () => {
                    // Navegar para o documento baseado no tipo de entidade
                    if (notification.entityId && notification.entityType) {
                        // Fechar dropdown após clicar na notificação
                        this.closeDropdown();
                        this.navigateToDocument(notification.entityType, notification.entityId);
                    }
                },
                error: error => {
                    console.error('❌ Erro ao marcar como lida:', error);
                    // Reverter estado local em caso de erro
                    this.updateNotificationInCache({ ...notification, isRead: false });
                },
            });
        }
    }

    /**
     * Clique em notificação já lida - apenas para navegação
     */
    onReadNotificationClick(notification: INotificationResponse): void {
        // Navegar para o documento baseado no tipo de entidade
        if (notification.entityId && notification.entityType) {
            // Fechar dropdown após clicar na notificação
            this.closeDropdown();
            this.navigateToDocument(notification.entityType, notification.entityId);
        }
    }

    /**
     * Navega para o documento baseado no tipo de entidade
     */
    private navigateToDocument(entityType: string, entityId: string): void {
        switch (entityType) {
            case 'supply':
                this.documentNavigationService.navigateToSupply(entityId);
                break;
            case 'occurrence':
                this.documentNavigationService.navigateToOccurrence(entityId);
                break;
            case 'occurrenceDispatch':
                this.documentNavigationService.navigateToOccurrenceDispatch(entityId);
                break;
            case 'patrol':
                this.documentNavigationService.navigateToPatrol(entityId);
                break;
            case 'shift':
                this.documentNavigationService.navigateToShift(entityId);
                break;
            case 'vehicleChecklist':
                this.documentNavigationService.navigateToVehicleChecklist(entityId);
                break;
            case 'motorcycleChecklist':
                this.documentNavigationService.navigateToMotorcycleChecklist(entityId);
                break;
            case 'motorizedService':
                this.documentNavigationService.navigateToMotorizedService(entityId);
                break;
            case 'doormanChecklist':
                this.documentNavigationService.navigateToDoormanChecklist(entityId);
                break;
            case 'armamentChecklist':
                this.documentNavigationService.navigateToArmamentChecklist(entityId);
                break;
            case 'panicEvent':
                this.documentNavigationService.navigateToPanicEvent(entityId);
                break;
            case 'document':
                this.documentNavigationService.navigateToDocuments();
                break;
            case 'foodBasket':
                this.router.navigate(['/hr/cesta-basica/visualizar', entityId]);
                break;
            case 'disciplinaryWarning':
                this.router.navigate(['/hr/advertencia-disciplinar/visualizar', entityId]);
                break;
            case 'vacationSchedule':
                this.router.navigate(['/hr/escala-ferias/visualizar', entityId]);
                break;
            case 'termination':
                this.router.navigate(['/hr/desligamento/visualizar', entityId]);
                break;
        }
    }

    /**
     * Atualiza notificação no cache local
     */
    private updateNotificationInCache(updatedNotification: INotificationResponse): void {
        // Atualizar notificação no cache do serviço
        this.notificationService['updateCacheWithNotification'](updatedNotification);

        // Recalcular e atualizar contador de não lidas
        this.recalculateUnreadCount();
    }

    /**
     * Recalcula contador de não lidas baseado no cache atual
     */
    private recalculateUnreadCount(): void {
        const currentNotifications = this.notifications();
        const unreadCount = currentNotifications.filter(n => !n.isRead).length;
        this.notificationService['unreadCount'].set(unreadCount);
    }

    /**
     * Remove notificação
     */
    removeNotification(notification: INotificationResponse): void {
        this.notificationService.remover(notification.id).subscribe({
            next: () => {},
            error: (error: any) => {
                console.error('❌ Erro ao remover notificação:', error);
            },
        });
    }

    /**
     * Marca todas como lidas
     */
    markAllAsRead(): void {
        this.notificationService.marcarTodasComoLidas().subscribe({
            next: () => {
                // Fechar dropdown após marcar todas como lidas
                this.closeDropdown();
            },
            error: error => {
                console.error('❌ Erro ao marcar todas como lidas:', error);
            },
        });
    }

    /**
     * Formata tempo relativo
     */
    formatTime(createdAt: Date | string): string {
        const now = new Date();
        const notificationTime = new Date(createdAt);
        const diffMs = now.getTime() - notificationTime.getTime();
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffMinutes < 1) return 'Agora';
        if (diffMinutes < 60) return `${diffMinutes}min`;
        if (diffHours < 24) return `${diffHours}h`;
        if (diffDays < 7) return `${diffDays}d`;

        return notificationTime.toLocaleDateString('pt-BR');
    }

    /**
     * Obtém ícone baseado no tipo de notificação
     */
    getNotificationIcon(notification: INotificationResponse): string {
        switch (notification.type) {
            case 'USER':
                return 'fas fa-user';
            case 'SYSTEM':
                return 'fas fa-cog';
            case 'BUSINESS':
                return 'fas fa-briefcase';
            case 'ALERT':
                return 'fas fa-exclamation-triangle';
            case 'EMERGENCY':
                return 'fas fa-exclamation-circle';
            default:
                return 'fas fa-bell';
        }
    }

    /**
     * Obtém as classes CSS básicas para a notificação
     */
    getNotificationClass(notification: INotificationResponse): { [key: string]: boolean } {
        return {
            flex: true,
            'items-center': true,
            'p-3': true,
            'border-b': true,
            'border-gray-200': true,
            'dark:border-gray-700': true,
            relative: true,
        };
    }

    /**
     * Obtém a cor da borda baseada no tipo de entidade e status de leitura
     */
    getNotificationBorderColor(notification: INotificationResponse): string {
        const colorMap: { [key: string]: string } = {
            supply: '#22c55e',
            occurrence: '#ef4444',
            occurrenceDispatch: '#f97316',
            patrol: '#3b82f6',
            shift: '#9333ea',
            vehicleChecklist: '#06b6d4',
            motorcycleChecklist: '#6366f1',
            motorizedService: '#ec4899',
            doormanChecklist: '#14b8a6',
            panicEvent: '#dc2626',
            document: '#f59e0b',
            unknown: '#6b7280',
        };

        const entityType = notification.entityType || 'unknown';
        const color = colorMap[entityType] || '#6b7280';
        const thickness = notification.isRead ? '3px' : '4px';

        return `${thickness} solid ${color}`;
    }

    // ============================================================================
    // 🚨 POPUP DE ALERTA PARA OCORRÊNCIAS E PÂNICO
    // ============================================================================

    /**
     * Verifica se deve mostrar popup de alerta para occurrenceDispatch
     * Apenas para novos despachos (created), não para atualizações ou finalizações
     */
    private shouldShowOccurrenceAlert(notification: INotificationResponse): boolean {
        const user = this.authService.getCurrentUser() as any;

        // Verificar se é um guarda e se é occurrenceDispatch
        if (user?.role !== 'GUARD' || notification.entityType !== 'occurrenceDispatch' || notification.isRead) {
            return false;
        }

        // Verificar se é um novo despacho (created) baseado no título
        const isNewDispatch =
            notification.title.includes('Novo Despacho') ||
            (notification.title.includes('Despacho de Ocorrência') && !notification.title.includes('Atualizado') && !notification.title.includes('Concluído'));

        return isNewDispatch;
    }

    /**
     * Verifica se deve mostrar popup de alerta para panicEvent.
     *
     * Só dispara na notificação de ACIONAMENTO (🚨 / "Pânico acionado").
     * Notificações de resolução/atendimento ("Pânico resolvido/atendido") vão
     * pro sino normalmente mas NÃO disparam popup — o usuário já tá ciente.
     *
     * Para TODOS os destinatários (vigilantes, supervisores, admins, síndicos).
     */
    private shouldShowPanicAlert(notification: INotificationResponse): boolean {
        if (notification.entityType !== 'panicEvent' || notification.isRead) {
            return false;
        }

        // Só alerta para o acionamento original. Resolução ("Pânico resolvido",
        // "Pânico atendido") segue só pro sino, sem popup.
        const isResolution =
            notification.title.includes('resolvido') ||
            notification.title.includes('atendido') ||
            notification.title.includes('Resolvido') ||
            notification.title.includes('Atendido');
        if (isResolution) return false;

        const isPanicNotification =
            notification.title.includes('Pânico') ||
            notification.title.includes('🚨') ||
            notification.message.includes('Pânico');

        return isPanicNotification;
    }

    /**
     * Mostra popup de alerta para ocorrência
     */
    private showOccurrenceAlert(notification: INotificationResponse): void {
        if (!this.shouldShowOccurrenceAlert(notification)) return;

        const eventId = notification.entityId;
        if (eventId && this.alertPopupQueue().some(p => p.entityId === eventId)) {
            return;
        }

        // Personalizar mensagem para o guarda
        const personalizedMessage = this.createPersonalizedMessage(notification);

        const alertData: AlertPopupData = {
            title: notification.title,
            message: personalizedMessage,
            type: 'occurrence',
            entityId: notification.entityId || undefined,
            entityType: notification.entityType || undefined,
        };

        this.alertPopupQueue.update(q => [...q, alertData]);
    }

    /**
     * Enfileira popup de pânico.
     *
     * Consulta o status atual do panicEvent no backend antes de enfileirar.
     * Se status !== 'OPEN', suprime (não enfileira). Resolve o bug onde reload
     * da página trazia popups de panics já resolvidos.
     */
    private showPanicAlert(notification: INotificationResponse): void {
        if (!this.shouldShowPanicAlert(notification)) return;

        const eventId = notification.entityId;

        // Evita duplicata: se já existe popup pra esse panicEvent na fila, ignora
        if (eventId && this.alertPopupQueue().some(p => p.entityId === eventId)) {
            return;
        }

        if (!eventId) {
            // Sem entityId não dá pra checar — enfileira mesmo assim
            this.enqueuePanicAlert(notification, '');
            return;
        }

        // Consulta status atual antes de enfileirar
        this.panicEventsService.buscar(eventId).subscribe({
            next: (event) => {
                if (event.status !== 'OPEN') {
                    // Panic já resolvido/atendido — não mostra popup
                    return;
                }
                this.enqueuePanicAlert(notification, eventId);
            },
            error: () => {
                // Falha ao buscar (evento deletado, etc) — não mostra popup
            },
        });
    }

    private enqueuePanicAlert(notification: INotificationResponse, eventId: string): void {
        const personalizedMessage = this.createPanicMessage(notification);
        const alertData: AlertPopupData = {
            title: notification.title || '🚨 Pânico Acionado',
            message: personalizedMessage,
            type: 'panic',
            entityId: eventId || notification.entityId || undefined,
            entityType: notification.entityType || undefined,
        };
        this.alertPopupQueue.update(q => [...q, alertData]);
    }

    /**
     * Cria mensagem personalizada para pânico
     */
    private createPanicMessage(notification: INotificationResponse): string {
        // Usar a mensagem da notificação ou criar uma padrão
        if (notification.message) {
            return notification.message;
        }

        return 'Um evento de pânico foi acionado. Ação imediata necessária!';
    }

    /**
     * Cria mensagem personalizada para o guarda
     * Apenas para novos despachos (created)
     */
    private createPersonalizedMessage(notification: INotificationResponse): string {
        // Extrair informações da mensagem original
        const originalMessage = notification.message;
        // Se a mensagem contém "despachou ocorrência para", personalizar
        if (originalMessage.includes('despachou ocorrência para')) {
            // Extrair nome do supervisor e guarda da mensagem
            const match = originalMessage.match(/(.+?)\s+despachou\s+ocorrência\s+para\s+(.+?)\s+às\s+(.+?)$/);
            if (match) {
                const supervisorName = match[1];
                const time = match[3];

                return `${supervisorName} despachou uma ocorrência para você às ${time}. Abra para ver detalhes.`;
            }
        }

        // Fallback para mensagem original
        return originalMessage;
    }

    /**
     * Verifica notificações não lidas após o load (reload / cold start / resume).
     * Para ocorrências (apenas guardas) e pânico (todos).
     * Enfileira TODOS os matches (não só o primeiro).
     *
     * @param force — true no resume do app; ignora flag de “já checou no boot”
     */
    private checkUnreadOccurrenceAlerts(force = false): void {
        if (!force && this.hasShownInitialAlert()) return;

        const user = this.authService.getCurrentUser() as any;
        const notifications = this.notifications();

        // Pânico primeiro (prioridade maior, para todos)
        const panicAlerts = notifications.filter(n => this.shouldShowPanicAlert(n));
        if (panicAlerts.length > 0) {
            panicAlerts.forEach(n => this.showPanicAlert(n));
        }

        // Ocorrências apenas para guardas
        if (user?.role === 'GUARD') {
            const occurrenceAlerts = notifications.filter(
                n =>
                    n.entityType === 'occurrenceDispatch' &&
                    !n.isRead &&
                    (n.title.includes('Novo Despacho') ||
                        (n.title.includes('Despacho de Ocorrência') && !n.title.includes('Atualizado') && !n.title.includes('Concluído')))
            );

            if (occurrenceAlerts.length > 0) {
                occurrenceAlerts.forEach(n => this.showOccurrenceAlert(n));
            }
        }

        // Marca como feito mesmo sem matches — evita reprocessar a cada open do dropdown
        this.hasShownInitialAlert.set(true);
    }

    /**
     * Fecha popup de alerta (consome o primeiro da fila).
     * Se houver mais popups, o próximo aparece automaticamente.
     */
    onAlertClose(closedItem?: AlertPopupData): void {
        this.alertPopupQueue.update(q => q.slice(1));
    }

    /**
     * Pula para o próximo popup (sem marcar como lida).
     * O AlertPopupComponent gerencia o índice interno; este handler só
     * precisa notificar o pai se quiser fazer alguma coisa extra.
     * Na prática, "Próximo" no popup é navegação local — sem mudança na fila.
     */
    onAlertNext(_nextedItem?: AlertPopupData): void {
        // No-op aqui: AlertPopupComponent mantém currentIndex internamente.
        // O pai não precisa saber de navegação pra frente.
    }

    /**
     * Volta para o popup anterior (sem marcar como lida).
     * Mesmo que onAlertNext: navegação local no AlertPopupComponent.
     */
    onAlertPrevious(): void {
        // No-op aqui: AlertPopupComponent mantém currentIndex internamente.
    }

    /**
     * Reage a panic_status_updated do socket.
     * - Atualiza cache de status
     * - Fecha popup ativo se for desse panic
     * - Marca visualmente a notificação como resolvida (sem chamar API de read)
     */
    private onPanicStatusUpdated(payload: {
        panicEventId: string;
        status: 'ATTENDED' | 'RESOLVED';
        updatedBy: string;
        updatedByName?: string;
    }): void {
        // 1. Fechar popup ativo se for desse panic
        this.alertPopupQueue.update(q => q.filter(p => p.entityId !== payload.panicEventId));

        // 2. Atualizar notification no cache (marcar como resolvida visualmente)
        this.notificationService.marcarPanicComoResolvido(
            payload.panicEventId,
            payload.status,
            payload.updatedByName,
        );
    }

    /**
     * Fecha TODOS os popups pendentes da fila.
     */
    onAlertCloseAll(): void {
        this.alertPopupQueue.set([]);
    }

    /**
     * Abre entidade relacionada ao alerta e limpa TODA a fila de popups.
     *
     * Semântica: usuário clicou em Abrir (vai resolver). Os outros popups
     * pendentes não fazem sentido — ele vai tratar essa fila de qualquer
     * forma via tela cheia. Limpar fila evita ficar vendo popups "atrasados"
     * depois do redirect.
     */
    onAlertOpen(data: { entityId: string; entityType: string }): void {
        // Navegar para a entidade específica
        if (data.entityType === 'panicEvent') {
            this.documentNavigationService.navigateToPanicEvent(data.entityId);
        } else {
            // Para outras entidades, usar o método genérico
            this.navigateToDocument(data.entityType, data.entityId);
        }

        // Marcar a notificação relacionada como lida
        this.markRelatedNotificationAsRead(data.entityId, data.entityType);

        // Limpa TODA a fila — o próximo não vai aparecer em cima da tela cheia
        this.alertPopupQueue.set([]);
    }

    /**
     * Marca notificação relacionada como lida baseada na entidade
     */
    private markRelatedNotificationAsRead(entityId: string, entityType: string): void {
        const relatedNotification = this.notifications().find(n => n.entityId === entityId && n.entityType === entityType && !n.isRead);

        if (relatedNotification) {
            this.markAsRead(relatedNotification);
        }
    }

    /**
     * Reage a novas notificações recebidas em tempo real
     */
    private onNewNotificationReceived(notification: INotificationResponse): void {
        // Verificar pânico primeiro (prioridade maior)
        if (this.shouldShowPanicAlert(notification)) {
            this.showPanicAlert(notification);
            return;
        }

        // Verificar ocorrências
        if (this.shouldShowOccurrenceAlert(notification)) {
            this.showOccurrenceAlert(notification);
        }
    }
}
